import React, { useState, useMemo, useCallback, useEffect } from 'react'
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import {
    CatalogFilterBar,
    CatalogFilterDrawer,
    CatalogSelectField,
    FilterChipItem,
    PropertyTagFilterField,
    getPropertyTagChip,
    uniqueNamedOptions,
} from '../../../ContractManager/CatalogFilterChrome'
import RefreshIcon from '@material-ui/icons/Refresh'
import VisibilityIcon from '@material-ui/icons/Visibility'
import DeleteIcon from '@material-ui/icons/Delete'
import { useOrgVendorW9s, VendorW9 } from '../../../../hooks/useOrgVendorW9s'
import { useUserTags } from '../../../../contexts/UserTagsContext'
import { DeleteConfirmDialog } from '../../../ContractManager/DeleteConfirmDialog'
import { DocumentPreviewModal } from '../../../ContractManager/DocumentPreviewModal'
import { formatDate } from '../../../ContractManager/utils'
import { useDebounce } from '../../../../hooks'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
    },
    searchField: {
        minWidth: 300,
    },
    tableWrapper: {
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        overflow: 'hidden',
    },
    tableContainer: {
        maxHeight: 'calc(100vh - 550px)',
    },
    headerRow: {
        backgroundColor: theme.palette.grey[100],
    },
    headerCell: {
        fontWeight: 600,
        color: theme.palette.text.secondary,
        fontSize: '13px',
    },
    row: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    vendorCell: {
        fontWeight: 500,
    },
    analyzingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    analyzingText: {
        fontStyle: 'italic',
        fontSize: '13px',
    },
    actionsCell: {
        textAlign: 'right',
        whiteSpace: 'nowrap',
        width: '1%',
        minWidth: 100,
    },
    propertyCell: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    tagChip: {
        height: 20,
        fontSize: '11px',
        fontWeight: 500,
    },
    emptyRow: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
}))

interface OrganizationVendorW9sProps {
    organization: number | null
    selectedTagId: number | null
    onSelectedTagIdChange: (value: number | null) => void
}

export const OrganizationVendorW9s: React.FC<OrganizationVendorW9sProps> = ({
    organization,
    selectedTagId,
    onSelectedTagIdChange,
}) => {
    const classes = useStyles()
    const { userTags } = useUserTags()

    const {
        vendorW9s,
        isLoadingVendorW9s,
        deleteVendorW9,
        processVendorW9,
        processingIds,
        isDeleting,
    } = useOrgVendorW9s({ organization })

    // Search state with debounce
    const [searchTerm, setSearchTerm] = useState('')
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const [propertyFilter, setPropertyFilter] = useState<'all' | number>('all')
    const [vendorFilter, setVendorFilter] = useState<
        'all' | 'unassigned' | number
    >('all')
    const debouncedSearchTerm = useDebounce(searchTerm, 200)

    // Pagination state
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(25)

    // Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [w9ToDelete, setW9ToDelete] = useState<VendorW9 | null>(null)

    useEffect(() => {
        setPage(0)
    }, [selectedTagId, propertyFilter, vendorFilter])

    // Get all tags for a property
    const getTagsForProperty = useCallback(
        (apartmentId: number) => {
            return userTags.filter((tag) =>
                tag.apartments.includes(apartmentId),
            )
        },
        [userTags],
    )

    const vendorOptions = useMemo(
        () => uniqueNamedOptions(vendorW9s.map((w9) => w9.vendor || {})),
        [vendorW9s],
    )
    const propertyOptions = useMemo(
        () =>
            uniqueNamedOptions(
                vendorW9s.map((w9) => ({
                    id: w9.apartment,
                    name: w9.apartment_name,
                })),
            ),
        [vendorW9s],
    )

    const filteredW9s = useMemo(() => {
        let filtered = vendorW9s

        if (selectedTagId !== null) {
            const selectedTag = userTags.find((tag) => tag.id === selectedTagId)
            if (selectedTag) {
                filtered = filtered.filter((w9) =>
                    selectedTag.apartments.includes(w9.apartment),
                )
            }
        }
        if (propertyFilter !== 'all') {
            filtered = filtered.filter((w9) => w9.apartment === propertyFilter)
        }
        if (vendorFilter === 'unassigned') {
            filtered = filtered.filter((w9) => !w9.vendor)
        } else if (vendorFilter !== 'all') {
            filtered = filtered.filter((w9) => w9.vendor?.id === vendorFilter)
        }

        if (debouncedSearchTerm.trim()) {
            const searchLower = debouncedSearchTerm.toLowerCase()
            filtered = filtered.filter(
                (w9) =>
                    w9.vendor?.name?.toLowerCase().includes(searchLower) ||
                    w9.name?.toLowerCase().includes(searchLower) ||
                    w9.apartment_name?.toLowerCase().includes(searchLower),
            )
        }

        return filtered
    }, [
        vendorW9s,
        debouncedSearchTerm,
        selectedTagId,
        userTags,
        propertyFilter,
        vendorFilter,
    ])

    const clearDrawerFilters = () => {
        setPropertyFilter('all')
        setVendorFilter('all')
        onSelectedTagIdChange(null)
        setPage(0)
    }

    const filterChips = useMemo(() => {
        const chips: FilterChipItem[] = []
        const tagChip = getPropertyTagChip(selectedTagId, userTags, () =>
            onSelectedTagIdChange(null),
        )
        if (tagChip) chips.push(tagChip)
        if (propertyFilter !== 'all') {
            chips.push({
                id: 'property',
                label: `Property: ${
                    propertyOptions.find((item) => item.id === propertyFilter)
                        ?.name || 'Property'
                }`,
                onDelete: () => setPropertyFilter('all'),
            })
        }
        if (vendorFilter === 'unassigned') {
            chips.push({
                id: 'vendor',
                label: 'Vendor: Unassigned',
                onDelete: () => setVendorFilter('all'),
            })
        } else if (vendorFilter !== 'all') {
            chips.push({
                id: 'vendor',
                label: `Vendor: ${
                    vendorOptions.find((item) => item.id === vendorFilter)
                        ?.name || 'Vendor'
                }`,
                onDelete: () => setVendorFilter('all'),
            })
        }
        return chips
    }, [
        selectedTagId,
        userTags,
        onSelectedTagIdChange,
        propertyFilter,
        vendorFilter,
        propertyOptions,
        vendorOptions,
    ])

    // Paginate filtered W-9s
    const paginatedW9s = useMemo(() => {
        const start = page * rowsPerPage
        const end = start + rowsPerPage
        return filteredW9s.slice(start, end)
    }, [filteredW9s, page, rowsPerPage])

    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const [previewDoc, setPreviewDoc] = useState<VendorW9 | null>(null)

    const handleView = useCallback((w9: VendorW9) => {
        setPreviewDoc(w9)
    }, [])

    const handleDeleteClick = useCallback((w9: VendorW9) => {
        setW9ToDelete(w9)
        setDeleteDialogOpen(true)
    }, [])

    const handleConfirmDelete = useCallback(() => {
        if (!w9ToDelete) return
        deleteVendorW9.mutate(w9ToDelete.id, {
            onSuccess: () => {
                setDeleteDialogOpen(false)
                setW9ToDelete(null)
            },
        })
    }, [w9ToDelete, deleteVendorW9])

    if (isLoadingVendorW9s) {
        return (
            <Box className={classes.loadingContainer}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Typography variant="h6">Vendor W-9s</Typography>
                <Typography variant="body2" color="textSecondary">
                    View W-9 forms from vendors across all properties.
                </Typography>
            </Box>

            <CatalogFilterBar
                searchPlaceholder="Search by vendor or property..."
                searchValue={searchTerm}
                onSearchChange={(value) => {
                    setSearchTerm(value)
                    setPage(0)
                }}
                onOpenFilters={() => setFilterDrawerOpen(true)}
                chips={filterChips}
                onClearAll={
                    filterChips.length > 0 ? clearDrawerFilters : undefined
                }
                resultLabel={`${filteredW9s.length} vendor W-9${
                    filteredW9s.length !== 1 ? 's' : ''
                }`}
            />
            <CatalogFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                title="Vendor W-9 filters"
                activeCount={filterChips.length}
                onClear={clearDrawerFilters}
            >
                <PropertyTagFilterField
                    value={selectedTagId}
                    onChange={(value) => {
                        onSelectedTagIdChange(value)
                        setPage(0)
                    }}
                />
                <CatalogSelectField
                    label="Property"
                    value={propertyFilter}
                    onChange={(value) =>
                        setPropertyFilter(
                            value === 'all' ? 'all' : Number(value),
                        )
                    }
                    options={[
                        { value: 'all', label: 'All properties' },
                        ...propertyOptions.map((property) => ({
                            value: property.id,
                            label: property.name,
                        })),
                    ]}
                />
                <CatalogSelectField
                    label="Vendor"
                    value={vendorFilter}
                    onChange={(value) =>
                        setVendorFilter(
                            value === 'all' || value === 'unassigned'
                                ? value
                                : Number(value),
                        )
                    }
                    options={[
                        { value: 'all', label: 'All vendors' },
                        { value: 'unassigned', label: 'Unassigned' },
                        ...vendorOptions.map((vendor) => ({
                            value: vendor.id,
                            label: vendor.name,
                        })),
                    ]}
                />
            </CatalogFilterDrawer>

            {/* Table */}
            <Paper className={classes.tableWrapper}>
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow className={classes.headerRow}>
                                <TableCell className={classes.headerCell}>
                                    Vendor
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Property
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Name
                                </TableCell>
                                <TableCell className={classes.headerCell}>
                                    Uploaded
                                </TableCell>
                                <TableCell
                                    className={`${classes.headerCell} ${classes.actionsCell}`}
                                    align="right"
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedW9s.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        className={classes.emptyRow}
                                    >
                                        {searchTerm
                                            ? 'No matching vendor W-9s found'
                                            : 'No vendor W-9s found'}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedW9s.map((w9) => (
                                    <TableRow
                                        key={w9.id}
                                        className={classes.row}
                                        onClick={() => handleView(w9)}
                                    >
                                        <TableCell
                                            className={classes.vendorCell}
                                        >
                                            {w9.vendor?.name || '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Box
                                                className={classes.propertyCell}
                                            >
                                                <span>{w9.apartment_name}</span>
                                                {(() => {
                                                    const tags = getTagsForProperty(
                                                        w9.apartment,
                                                    )
                                                    return tags.length > 0 ? (
                                                        <Box
                                                            display="flex"
                                                            flexWrap="wrap"
                                                            style={{ gap: 4 }}
                                                        >
                                                            {tags.map((tag) => (
                                                                <Chip
                                                                    key={tag.id}
                                                                    label={
                                                                        tag.name
                                                                    }
                                                                    size="small"
                                                                    className={
                                                                        classes.tagChip
                                                                    }
                                                                    style={{
                                                                        backgroundColor:
                                                                            tag.color,
                                                                        color:
                                                                            'white',
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    ) : null
                                                })()}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {w9.name}
                                            </Typography>
                                            {processingIds.has(w9.id) ? (
                                                <Box
                                                    className={
                                                        classes.analyzingContainer
                                                    }
                                                >
                                                    <CircularProgress
                                                        size={14}
                                                        color="primary"
                                                    />
                                                    <Typography
                                                        className={
                                                            classes.analyzingText
                                                        }
                                                    >
                                                        Analyzing document...
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                w9.ai_summary && (
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                        style={{
                                                            display: 'block',
                                                        }}
                                                    >
                                                        {w9.ai_summary}
                                                    </Typography>
                                                )
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(w9.created_date)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            className={classes.actionsCell}
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            <Tooltip title="Reanalyze">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            processVendorW9.mutate(
                                                                w9.id,
                                                            )
                                                        }
                                                        disabled={processingIds.has(
                                                            w9.id,
                                                        )}
                                                    >
                                                        <RefreshIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="View">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleView(w9)
                                                    }
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        handleDeleteClick(w9)
                                                    }
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={filteredW9s.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>

            <DeleteConfirmDialog
                isOpen={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                documentName={w9ToDelete?.name || ''}
                isDeleting={isDeleting}
            />
            <DocumentPreviewModal
                open={previewDoc != null}
                onClose={() => setPreviewDoc(null)}
                title={previewDoc?.name || 'Vendor W-9'}
                fileUrl={previewDoc?.file || null}
                summary={previewDoc?.ai_summary}
            />
        </Box>
    )
}
